import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FileText, Clock, AlertCircle, CheckCircle, TrendingUp, Plus,
  RefreshCw, Search, LayoutDashboard, UserPlus, FilePlus, Loader,
  ChevronRight, Users, Building2, Menu, X, Bell
} from 'lucide-react';
import api from '../../services/api';
import './Dashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({ total: 0, in_process: 0, pending: 0, completed: 0, new_cases: 0 });
  const [recentCases, setRecentCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const result = await api.notifications.unreadCount();
      setUnreadCount(result?.count || result?.data?.count || 0);
    } catch (err) { console.error('Failed to fetch notification count:', err); }
  }, []);

  const fetchNotifications = async () => {
    try {
      const result = await api.notifications.list();
      const items = result?.data?.notifications || result?.notifications || result || [];
      setNotifications(Array.isArray(items) ? items.slice(0, 10) : []);
    } catch (err) { console.error('Failed to fetch notifications:', err); }
  };

  const handleBellClick = () => {
    if (!showNotifications) fetchNotifications();
    setShowNotifications(!showNotifications);
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif.read_at) {
        await api.notifications.markRead(notif.id);
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {}
    setShowNotifications(false);
    if (notif.case_id) navigate('/cases/' + notif.case_id);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.notifications.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
    } catch (err) {}
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, casesRes] = await Promise.all([
        api.get('/cases/stats'),
        api.get('/cases?limit=10&sort=created_at&order=desc'),
      ]);
      setStats(statsRes.data.stats || statsRes.data.data || statsRes.data || {});
      const casesList = casesRes.data.cases || casesRes.data.data || casesRes.data || [];
      setRecentCases(Array.isArray(casesList) ? casesList : []);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { id: 'divider1', divider: true },
    { id: 'create-case', label: 'Create Case', icon: FilePlus, path: '/cases/new' },
    { id: 'create-user', label: 'Create User', icon: UserPlus, path: '/users' },
    { id: 'create-university', label: 'Create University', icon: Building2, path: '/universities' },
    { id: 'divider2', divider: true },
    { id: 'pending', label: 'Pending Cases', icon: Clock, path: '/cases?status=pending' },
    { id: 'completed', label: 'Completed Cases', icon: CheckCircle, path: '/cases?status=completed' },
    { id: 'all-cases', label: 'All Cases', icon: FileText, path: '/cases' },
    { id: 'divider3', divider: true },
    { id: 'users', label: 'All Users', icon: Users, path: '/users' },
  ];

  const isActivePath = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname + location.search === path || location.pathname === path;
  };

  const handleSidebarClick = (path) => { setSidebarOpen(false); navigate(path); };

  const statCards = [
    { label: 'Total Cases', value: stats.total || 0, icon: FileText, color: 'blue' },
    { label: 'In Progress', value: stats.in_process || 0, icon: Clock, color: 'orange' },
    { label: 'Pending', value: stats.pending || 0, icon: AlertCircle, color: 'yellow' },
    { label: 'Completed', value: stats.completed || 0, icon: CheckCircle, color: 'green' },
    { label: 'New Cases', value: stats.new_cases || 0, icon: TrendingUp, color: 'purple' },
  ];

  const getStatusBadge = (status) => {
    const map = {
      pending_clarification: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
      in_process: { bg: '#dbeafe', color: '#1e40af', label: 'In Progress' },
      verification_started: { bg: '#e0e7ff', color: '#3730a3', label: 'Verification Started' },
      documents_uploaded: { bg: '#fce7f3', color: '#9d174d', label: 'Docs Uploaded' },
      completed: { bg: '#d1fae5', color: '#065f46', label: 'Completed' },
      closed: { bg: '#f3f4f6', color: '#374151', label: 'Closed' },
      rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
      new: { bg: '#ede9fe', color: '#5b21b6', label: 'New' },
    };
    const s = map[status] || { bg: '#f3f4f6', color: '#374151', label: status || 'Unknown' };
    return (<span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>{s.label}</span>);
  };

  const filtered = recentCases.filter((c) => {
    const matchSearch = !searchTerm || (c.applicant_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (c.case_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !statusFilter || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="admin-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo"><LayoutDashboard size={20} /><span>Admin Panel</span></div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
        </div>
        <nav className="sidebar-nav">
          {sidebarItems.map((item) => item.divider ? (
            <div key={item.id} className="sidebar-divider" />
          ) : (
            <button key={item.id} className={`sidebar-item ${isActivePath(item.path) ? 'active' : ''}`} onClick={() => handleSidebarClick(item.path)}>
              <item.icon size={18} /><span>{item.label}</span><ChevronRight size={14} className="sidebar-arrow" />
            </button>
          ))}
        </nav>
      </aside>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <main className="admin-main">
        <div className="admin-topbar">
          <div className="admin-topbar-left">
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
            <div><h1 className="admin-title">Dashboard</h1><p className="admin-subtitle">Overview of all verification cases</p></div>
          </div>
          <div className="admin-topbar-right">
              <div style={{ position: 'relative', marginRight: '8px' }}>
                <button onClick={handleBellClick} style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Notifications">
                  <Bell size={20} style={{ color: '#64748b' }} />
                  {unreadCount > 0 && (
                    <span style={{ position: 'absolute', top: '2px', right: '2px', backgroundColor: '#ef4444', color: 'white', fontSize: '10px', fontWeight: 700, minWidth: '16px', height: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', width: '360px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', zIndex: 1000, maxHeight: '400px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{ fontWeight: 600, fontSize: '15px' }}>Notifications</span>
                      {unreadCount > 0 && <button onClick={handleMarkAllRead} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>Mark all read</button>}
                    </div>
                    <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                      {notifications.length === 0 ? (
                        <div style={{ padding: '30px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No notifications yet</div>
                      ) : (
                        notifications.map((notif) => (
                          <div key={notif.id} onClick={() => handleNotificationClick(notif)} style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', backgroundColor: notif.read_at ? 'white' : '#f0f7ff', transition: 'background 0.15s' }}>
                            <div style={{ fontSize: '13px', fontWeight: notif.read_at ? 400 : 600, color: '#1e293b', marginBottom: '4px' }}>{notif.title || notif.message || 'New notification'}</div>
                            <div style={{ fontSize: '12px', color: '#94a3b8' }}>{notif.created_at ? new Date(notif.created_at).toLocaleString() : ''}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            <button className="btn-icon" onClick={fetchData} title="Refresh data"><RefreshCw size={18} className={loading ? 'spin' : ''} /></button>
            <button className="btn-primary" onClick={() => navigate('/cases/new')}><Plus size={18} /><span>Add New Case</span></button>
          </div>
        </div>
        <div className="quick-actions">
          <button className="quick-action-card" onClick={() => navigate('/cases/new')}><div className="qa-icon qa-blue"><FilePlus size={22} /></div><span>Create Case</span></button>
          <button className="quick-action-card" onClick={() => navigate('/users')}><div className="qa-icon qa-green"><UserPlus size={22} /></div><span>Create User</span></button>
          <button className="quick-action-card" onClick={() => navigate('/universities')}><div className="qa-icon qa-purple"><Building2 size={22} /></div><span>Create University</span></button>
          <button className="quick-action-card" onClick={() => navigate('/cases?status=pending')}><div className="qa-icon qa-orange"><Clock size={22} /></div><span>Pending Cases</span></button>
          <button className="quick-action-card" onClick={() => navigate('/cases?status=completed')}><div className="qa-icon qa-teal"><CheckCircle size={22} /></div><span>Completed</span></button>
        </div>
        <div className="stats-grid">
          {statCards.map((card) => (
            <div key={card.label} className={`stat-card stat-${card.color}`}>
              <div className={`stat-icon-wrap ${card.color}`}><card.icon size={22} /></div>
              <div className="stat-info"><span className="stat-value">{loading ? '-' : card.value}</span><span className="stat-label">{card.label}</span></div>
            </div>
          ))}
        </div>
        <div className="cases-section">
          <div className="cases-header">
            <h2 className="cases-title">Recent Cases</h2>
            <div className="cases-filters">
              <div className="search-box"><Search size={16} /><input type="text" placeholder="Search cases..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
              <select className="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="documents_uploaded">Docs Uploaded</option>
                <option value="in_process">In Process</option>
                <option value="verification_started">Verification Started</option>
                <option value="pending_clarification">Pending</option>
                <option value="completed">Completed</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
          <div className="cases-table-wrap">
            {loading ? (
              <div className="loading-state"><Loader size={24} className="spin" /><span>Loading cases...</span></div>
            ) : filtered.length === 0 ? (
              <div className="empty-state">No cases found</div>
            ) : (
              <table className="cases-table">
                <thead><tr><th>Case #</th><th>Applicant</th><th>College</th><th>Apply For</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} onClick={() => navigate(`/cases/${c.id}`)} className="clickable-row">
                      <td className="case-number">{c.case_number}</td>
                      <td>{c.applicant_name || '-'}</td>
                      <td>{c.college_name || '-'}</td>
                    <td>{c.apply_for || '-'}</td>
                      <td>{getStatusBadge(c.status)}</td>
                      <td className="date-cell">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
